import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FiSearch } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";

const fadeInOutDuration = "300ms";

const SearchIcon = styled(FiSearch)`
  color: #a0aec0;
  margin-right: 0.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  color: #2d3748;
  &::placeholder {
    color: #a0aec0;
  }
`;

const SearchBar = styled.div`
  display: flex;
  flex-direction: row;
`;

export const SearchContentContainer = styled.div`
  flex-direction: column;
  max-height: 450px;
  overflow-y: auto;
  border-radius: 15px;
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #a0aec0;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-track {
    background: #f9f9f9;
  }
`;

export const SearchContent = styled.div`
  display: flex;
  flex-direction: row;
  padding: 10px;
  justify-content: flex-start;
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  border-radius: 15px;
  margin-top: 5px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const NoResultsMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #a0aec0;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  visibility: hidden;
  transition: opacity ${fadeInOutDuration} ease-in-out,
    visibility ${fadeInOutDuration} ease-in-out;
  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const Modal = styled.div`
  background: white;
  padding: 10px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 650px;
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity ${fadeInOutDuration} ease-in-out,
    visibility ${fadeInOutDuration} ease-in-out;
  opacity: 0;
  visibility: hidden;
  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

interface SearchResult {
  id: string;
  folderId: string;
  fileId: string;
  fileContent: string;
  folderName: string;
  fileName: string;
}

const NavSearch = ({ onClose }: any) => {
  const [search, setSearch] = useState("");
  const [results] = useState<SearchResult[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
    setTimeout(() => setIsModalVisible(true), 10);
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    const handleTransitionEnd = () => {
      if (isModalVisible && inputRef.current) {
        inputRef.current.focus();
      }
    };

    if (modal) {
      modal.addEventListener("transitionend", handleTransitionEnd);
    }

    return () => {
      if (modal) {
        modal.removeEventListener("transitionend", handleTransitionEnd);
      }
    };
  }, [isModalVisible]);

  const startClosingModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, parseInt(fadeInOutDuration));
  };

  // useEffect(() => {
  //   if (search.length >= 3) {
  //     searchFileContent(search)
  //       .then((data) => {
  //         if (data && !data.error) {
  //           setResults(data);
  //           setSearchInitiated(true);
  //         } else {
  //           setResults([]);
  //           setSearchInitiated(false);
  //         }
  //       })
  //       .catch((error) => {
  //         console.error("Error fetching search results:", error);
  //         setResults([]);
  //       });
  //   } else {
  //     setResults([]);
  //   }
  // }, [search]);

  const handleItemClick = (folderId: string, fileId: string) => {
    startClosingModal();
    navigate(`/uploads/${folderId}`, { state: { fileId } });
  };

  const getSnippet = (
    text: string,
    term: string,
    linesBefore = 2,
    linesAfter = 2
  ) => {
    const termRegex = new RegExp(`(${term})`, "gi");
    const snippetRegex = new RegExp(`(.{0,100}${term}.{0,100})`, "gi");
    const matches = text.match(snippetRegex);
    if (matches) {
      return matches
        .map((match) => {
          const highlighted = match.replace(termRegex, "<strong>$1</strong>");
          const lines = highlighted.split("\n");
          const start = Math.max(
            0,
            lines.findIndex((line) => line.includes("<strong>")) - linesBefore
          );
          const end = Math.min(
            lines.length,
            lines.findIndex((line) => line.includes("<strong>")) +
              linesAfter +
              1
          );
          return `<strong>Q:</strong> ${lines.slice(start, end).join("\n")}`;
        })
        .join("...");
    }
    return `<strong>Q:</strong> ${term}`;
  };

  return (
    <Backdrop
      onClick={startClosingModal}
      style={{
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
      }}
    >
      <Modal
        ref={modalRef}
        className={isModalVisible ? "visible" : ""}
        onClick={(e) => e.stopPropagation()}
      >
        <SearchBar>
          <SearchIcon size={20} />
          <SearchInput
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search everything"
          />
          {search.length > 0 ? (
            <AiOutlineClose
              onClick={() => {
                setSearch("");
                setSearchInitiated(false);
              }}
              className="cursor-pointer self-center"
            />
          ) : null}
        </SearchBar>
        <SearchContentContainer>
          {searchInitiated && results.length === 0 ? (
            <NoResultsMessage>No questions were found</NoResultsMessage>
          ) : (
            results.map((item) => (
              <SearchContent
                onClick={() => handleItemClick(item.folderId, item.fileId)}
                key={item.id}
              >
                <div style={{ marginLeft: "20px" }}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: getSnippet(item.fileContent, search),
                    }}
                  />
                </div>
              </SearchContent>
            ))
          )}
        </SearchContentContainer>
      </Modal>
    </Backdrop>
  );
};

export default NavSearch;
